import '@cbhq/cds-web/globalStyles';
import '@cbhq/two-factor-register/commonStyles';
import '@cbhq/two-factor-register/styles';
import '@onramp/utils/bugsnag';
import { useMemo } from 'react';
import type { FC } from 'react';
import { InitParamsErrorView } from '@onramp/components/InitParamsErrorView';
import { NoSSR } from '@onramp/components/NoSSR';
import { PAGE_TITLE_BASE } from '@onramp/components/PageTitle';
import { useEffectOnMount } from '@onramp/hooks/useEffectOnMount';
import useFlushQueueOnPageHidden from '@onramp/hooks/useFlushQueueOnPageHidden';
import { useI18n } from '@onramp/hooks/useI18n';
import { getRoute } from '@onramp/hooks/useOnRampRouter';
import { postShowBackButtonEvent } from '@onramp/hooks/useShowBackButtonOnLoginForScw';
import useInitApis from '@onramp/hooks/utils/useInitApis';
import { useLocalStorageAtoms } from '@onramp/hooks/utils/useLocalStorageAtoms';
import { appState3dsAtom } from '@onramp/state/recoil/atoms/appState3dsAtom';
import type { SecureInitParameters } from '@onramp/state/recoil/atoms/secureInitAtoms';
import { secureInitParametersAtom } from '@onramp/state/recoil/atoms/secureInitAtoms';
import { jotaiStore } from '@onramp/state/recoil/utils';
import type { InitParamErrors } from '@onramp/types/initParamErrors';
import type { InitParamsType, InternalInitParamsType, WalletAnalyticsData } from '@onramp/types/initParams';
/* eslint-disable-next-line  @typescript-eslint/no-duplicate-imports */ // -- keeping the original import for the side effects
import { setBugsnagUserDetails } from '@onramp/utils/bugsnag';
import { clientSessionIdStore } from '@onramp/utils/clientSessionIdStore';
import { consoleUserSafety } from '@onramp/utils/consoleUserSafety';
import { setCookie } from '@onramp/utils/cookies/clientUtils';
import { isProduction, isServer } from '@onramp/utils/environment/sharedEnv';
import type { ExperimentsResponse } from '@onramp/utils/experiments/consts';
import { experimentsAtom } from '@onramp/utils/experiments/store';
import { identifyUser, initClientAnalytics } from '@onramp/utils/init/analytics';
import { isSmartContractWalletApp } from '@onramp/utils/isSmartContractWalletApp';
import { isWalletApp } from '@onramp/utils/isWalletApp';
import { handleLoadRiskJs, riskJsScriptSrc } from '@onramp/utils/riskJs';
import { getPathType } from '@onramp/utils/routes';
import usePageViewMetric from '@onramp/utils/usePageViewMetric';
import { useReportWebVitals } from '@onramp/utils/useWebVitals';
import { GuestCheckoutProviders, LandingProvidersV2 } from '@onramp/v2/client/contexts/app-providers/AppProviders';
import { getErrorVitals, initErrorVitals } from '@onramp/v2/client/utils/initErrorVitals';
import { InitParamsErrorPostMessageView } from '@onramp/v2/client/views/api-onramp/components/InitParamsErrorsPostMessage';
import { guestOnrampV2StoreAtom } from '@onramp/v2/client/views/guest/onramp/state/store';
import { DevTools } from 'jotai-devtools';
import { css } from 'linaria';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import Script from 'next/script';
import { v4 as uuidv4 } from 'uuid';
import { persistentData } from '@cbhq/client-analytics';
import type { InitialProps } from './_app/initialization/getInitialProps';
import { getInitialProps } from './_app/initialization/getInitialProps';
import { BuyWidgetProvider, FlowAgnosticProvider } from './_app/providers';
export const globalStyles = "gdfkt4l";

// This has been tested to confirm it isn't triggered on nav
initClientAnalytics();
// At least one of deviceId or userId are required for amplitude to not ignore an event
// and we need to idenitfUser to set that ID. A subsequent identifyUser with a userId will
// update the value while still keeping new events in the same session.
// The the condition is needed to prevent an error on server.
if (globalThis?.navigator !== undefined) {
  identifyUser({});
}
initErrorVitals();

/** Persisted between getInitialProps calls. */
let appRootInitParams: InitParamsType | undefined;
/** Persisted between getInitialProps calls. */
let appRootInitParamErrors: InitParamErrors | undefined;
/** Persisted between getInitialProps calls. */
let appRootInternalInitParams: InternalInitParamsType | undefined;
/** Persisted between getInitialProps calls */
let appRootWalletAnalyticsData: WalletAnalyticsData | undefined;
/** Persisted between getInitialProps calls */
let appRootExperiments: ExperimentsResponse | undefined;
function App({
  Component,
  pageProps,
  router,
  csrfToken,
  guestCheckoutCountryAllowlist,
  featureFlags,
  isGuestCheckoutRoute,
  isV2Route,
  secureInitParameters,
  initParams,
  internalInitParams,
  initParamErrors,
  appState3ds,
  persistedGuestSession,
  walletAnalyticsData,
  experiments
}: AppProps & InitialProps) {
  appRootInitParams ??= initParams;
  appRootInitParamErrors ??= initParamErrors;
  appRootInternalInitParams ??= internalInitParams;
  appRootWalletAnalyticsData ??= walletAnalyticsData;
  appRootExperiments ??= experiments;
  if (!isServer()) {
    // reuse CCA's session id, if already set
    // Setting session UUID in CCA's persistentData
    persistentData.sessionUUID ??= uuidv4();

    // Set our CSRF token, used for all follow-up requests
    if (typeof csrfToken === 'string') {
      clientSessionIdStore.setCsrfToken(csrfToken);
    }

    // the App component is rerendered without server props on navigation, so we need to check so that we don't set this to undefined
    if (appRootInternalInitParams?.clientAppDetails) {
      clientSessionIdStore.setClientAppDetails(appRootInternalInitParams?.clientAppDetails);
    }
    if (guestCheckoutCountryAllowlist) {
      clientSessionIdStore.setGuestCheckoutCountryAllowlist(guestCheckoutCountryAllowlist);
    }
    if (featureFlags) {
      clientSessionIdStore.setFeatureFlags(featureFlags);
    }
    if (appRootInternalInitParams?.userUuid) {
      clientSessionIdStore.setUserUuid(appRootInternalInitParams.userUuid);
      getErrorVitals().globalMetadata.userUuid = appRootInternalInitParams.userUuid;
      identifyUser({
        userId: appRootInternalInitParams.userUuid
      });
      setBugsnagUserDetails(appRootInternalInitParams.userUuid);
    }
    const appId = appRootInternalInitParams?.clientAppDetails?.appId;
    let walletUserId: string | undefined;
    if (appId) {
      getErrorVitals().globalMetadata.app_id = appId;
      if (isWalletApp(appId) && appRootWalletAnalyticsData !== undefined) {
        clientSessionIdStore.setWalletAnalyticsData(appRootWalletAnalyticsData);
        getErrorVitals().globalMetadata.walletUserId = appRootWalletAnalyticsData.walletUserId;
        persistentData.sessionId = appRootWalletAnalyticsData.sessionId;
        persistentData.sessionUUID = appRootWalletAnalyticsData.sessionUUID;

        // this needs to be set before calling identifyUser
        setCookie({
          cookieName: 'deviceId',
          value: appRootWalletAnalyticsData.deviceId
        });
        identifyUser({
          userId: appRootWalletAnalyticsData.walletUserId
        });
        setBugsnagUserDetails(appRootWalletAnalyticsData.walletUserId);
        walletUserId = appRootWalletAnalyticsData.walletUserId;
      }

      // Smart Contract Wallet renders a back button on top of the iframe when we're embedded
      // & have redirected to login.*, but needs to hide the button when we've rendered again
      if (isSmartContractWalletApp(appId) && window.parent !== window) {
        postShowBackButtonEvent(false);
      }
    }
    if (secureInitParameters) {
      jotaiStore.set(secureInitParametersAtom, (secureInitParameters as SecureInitParameters));
    }
    if (appState3ds) {
      jotaiStore.set(appState3dsAtom, appState3ds);
    }
    if (persistedGuestSession) {
      clientSessionIdStore.setGuestCheckoutEntityHash(persistedGuestSession.entityHash);
      getErrorVitals().globalMetadata.entityHash = persistedGuestSession.entityHash;
      jotaiStore.set(guestOnrampV2StoreAtom, prev => ({
        ...prev,
        persistedGuestSession
      }));
      // We prefer wallet user ID for amplitude events, but in bugsnag we use the entity hash
      identifyUser({
        userId: walletUserId ?? persistedGuestSession.entityHash
      });
      setBugsnagUserDetails(persistedGuestSession.entityHash);
    }
    if (appRootExperiments) {
      jotaiStore.set(experimentsAtom, appRootExperiments);
    }
  }
  return <>
      <Head>
        <title>{PAGE_TITLE_BASE}</title>
        <meta key="viewport-meta" // this key is required
      name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
      </Head>

      <NoSSR>
        <AppRoot pathname={router.pathname} initParams={appRootInitParams} initParamErrors={appRootInitParamErrors} internalInitParamsFromServer={appRootInternalInitParams}>
          {!isProduction && <DevTools />}
          <Component {...pageProps} />
        </AppRoot>

        {(isGuestCheckoutRoute || isV2Route) && <Script id="risk-js" defer src={riskJsScriptSrc} onLoad={handleLoadRiskJs} />}
      </NoSSR>
    </>;
}
type AppRootProps = {
  pathname: string;
  initParams: InitParamsType | undefined;
  initParamErrors: InitParamErrors | undefined;
  internalInitParamsFromServer: InternalInitParamsType | undefined;
};
const AppRoot: FC<AppRootProps> = ({
  children,
  pathname,
  initParams,
  initParamErrors,
  internalInitParamsFromServer
}) => {
  useEffectOnMount(() => {
    if (isProduction) {
      consoleUserSafety();
    }

    // We've seen initialization issues with nonce + redirects &
    // are aligned that query param initialization is fine from a
    // security perspective.
    // initParams contain the values from the nonce. This runs
    // in the browser to pull out the nonce param & persist
    // its contents in the query string.
    const haveNonce = new URLSearchParams(window.location.search).has('nonce');
    if (haveNonce && initParams) {
      replaceNonceWithQueryParams(initParams);
    }
  });
  useInitApis();
  usePageViewMetric();
  useReportWebVitals();
  useLocalStorageAtoms();
  useFlushQueueOnPageHidden();
  const {
    locale,
    messages
  } = useI18n();
  const haveInitParamsError = useMemo(() => Object.keys(initParamErrors ?? {}).length > 0, [initParamErrors]);
  if (!messages) {
    return null;
  }
  if (haveInitParamsError && initParamErrors && pathname !== getRoute('Error')) {
    const isApiFirstFlow = pathname.includes('api-onramp');
    if (isApiFirstFlow) {
      // If the app is API first, we don't show the InitParamsErrorView
      // and instead just return null to avoid showing an error page.
      return <InitParamsErrorPostMessageView errors={initParamErrors} locale={locale} messages={messages} />;
    }
    return <InitParamsErrorView errors={initParamErrors} locale={locale} messages={messages} />;
  }
  const type = getPathType(pathname);
  if (type === 'buy') {
    return <BuyWidgetProvider initParamsFromServer={initParams} internalInitParamsFromServer={internalInitParamsFromServer} locale={locale} messages={messages}>
        {children}
      </BuyWidgetProvider>;
  }
  if (type === 'landing') {
    return <LandingProvidersV2 initParamsFromServer={initParams} internalInitParamsFromServer={internalInitParamsFromServer} locale={locale} messages={messages}>
        {children}
      </LandingProvidersV2>;
  }
  if (type === 'v2') {
    if (pathname.includes('guest')) {
      return <GuestCheckoutProviders initParamsFromServer={initParams} internalInitParamsFromServer={internalInitParamsFromServer} locale={locale} messages={messages}>
          {children}
        </GuestCheckoutProviders>;
    }
    return <LandingProvidersV2 initParamsFromServer={initParams} internalInitParamsFromServer={internalInitParamsFromServer} locale={locale} messages={messages} shouldSuspend={!pathname.includes('api-onramp')} widgetBackgroundVariant={pathname.includes('api-onramp') ? 'transparent' : 'solid'}>
        {children}
      </LandingProvidersV2>;
  }
  return <FlowAgnosticProvider locale={locale} messages={messages}>
      {children}
    </FlowAgnosticProvider>;
};
App.getInitialProps = getInitialProps;

// Temporary fix to avoid flash of unstyled content
// during route transitions. Keep an eye on this
// issue and remove this code when resolved:
// https://github.com/vercel/next.js/issues/17464
const routeChange = () => {
  const tempFix = () => {
    const allStyleElems = document.querySelectorAll('style[media="x"]');
    allStyleElems.forEach(elem => {
      elem.removeAttribute('media');
    });
  };
  tempFix();
};
Router.events.on('routeChangeComplete', routeChange);
Router.events.on('routeChangeStart', routeChange);
function replaceNonceWithQueryParams(initParams: InitParamsType) {
  const newUrl = new URL(window.location.href);
  for (const [key, value] of Object.entries(initParams)) {
    if (typeof value === 'string') {
      newUrl.searchParams.set(key, `${value}`);
    } else {
      newUrl.searchParams.set(key, JSON.stringify(value));
    }
  }

  // secure_standlone indicates that we should init from  message
  if (newUrl.searchParams.get('type') === 'secure_standalone') {
    newUrl.searchParams.delete('type');
  }
  newUrl.searchParams.delete('nonce');
  window.history.replaceState(window.history.state, '', newUrl);
}
export default App;

require("./_app.page.linaria.module.css!=!../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./_app.page.tsx");